import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../../../environments/environment';
import { appModernizationDetails } from '../../../../../config';
import { Observable } from 'rxjs';
import { CommonService } from './common.service';
import { BULK_MAINTENANCE_ERROR_MESSAGES } from './bulk-maintenance-error-messages';
import { SharedAuthService } from '../../../../../shared/shared.auth.service';

@Injectable({
  providedIn: 'root'
})
export class AppDemograhicService {
  private TableColumnLOBMapping = {
    'GLOBAL APP ID': 'ASK GLOBAL ID',
    'LOB ID': 'Lob_ID',
    'Line Of Business': '[LOB]',
    'Sub LOB ID': 'SUB_LOB_ID',
    'Update_Type(U)': 'update_Type',
    Category_ID: 'category_id',
    Application_Type_Id: 'application_type_id',
    Application_Hosting_ID: 'app_hosting_id',
    'Message': 'Message',
    'Status': 'Status',
    'wqRequestBy': 'wqRequestBy',
  };
  private deomgraphicRequiredHeaders = [
    'Update_Type(U)',
    'GLOBAL APP ID', 'LOB ID',
    'Sub LOB ID', 'Category_ID', 'Application_Type_Id',
    'Application_Hosting_ID'
  ]
  private BULK_APP_DEMOGRAPHICS_UPDATE_TYPE = ['Lob/SubLob', 'Attribute'];
  private apiUrl: string;
  private largeApiUrl: string;
  private config: any;
  userDtls: any;
 
  constructor(private http: HttpClient, private commonService: CommonService,private sharedAuthService: SharedAuthService ) {
    this.config = appModernizationDetails;
    this.apiUrl = environment.apiUrl;
    this.largeApiUrl = environment.LargeapiUrl;
    const currentUser = sessionStorage.getItem('currentUser') || '';
    this.userDtls = {
      cn: this.sharedAuthService.getMsid(),
      mail: sessionStorage.getItem('loggedInUserEmail') || '',
      givenName: currentUser ? currentUser.trim().split(',')[1].trim() : '',
      sn: currentUser ? currentUser.trim().split(',')[0].trim() : '',
    }
  }

  /**
   * Normalizes key identifier fields on the uploaded row to uppercase,
   * except for values explicitly marked as 'No Mapping'.
   *
   * @param obj Raw row object from the uploaded file.
   * @returns The same object instance with updated ID fields.
   */
  public transformIds(obj: any) {
    for (const key of Object.keys(obj)) {
      if (['GLOBAL APP ID', 'LOB ID', 'Sub LOB ID', 'Category_ID', 'Application_Type_Id', 'Application_Hosting_ID'].includes(key)) {
        const item = obj[key];
        if (item && item != 'No Mapping') {
          obj[key] = item.toUpperCase();
        }
      }
    }
    return obj;
  }

  /**
   * Converts raw Update_Type(U) values into the standardized
   * BULK_APP_DEMOGRAPHICS_UPDATE_TYPE labels (e.g. Lob/SubLob, Attribute).
   *
   * @param obj Row object whose Update_Type(U) needs normalization.
   * @returns The same object instance with a normalized Update_Type(U).
   */
  public modifyUpdateType(obj: any) {
    const type = obj['Update_Type(U)'];
    if (type.toUpperCase() == 'LOB/SUBLOB') {
      obj['Update_Type(U)'] = this.BULK_APP_DEMOGRAPHICS_UPDATE_TYPE[0];
    }
    if (type.toUpperCase() == 'ATTRIBUTE') {
      obj['Update_Type(U)'] = this.BULK_APP_DEMOGRAPHICS_UPDATE_TYPE[1];
    }
    return obj;
  }

  /**
   * Validates uploaded demographic records, removing duplicates, applying
   * row-level business rules, and splitting into success and error groups.
   *
   * @param excelData Parsed Excel rows from the uploaded file.
   * @param msid Current user MSID used for audit fields.
   * @param currentUser Current user display name, used to build createdByName.
   * @returns An object containing validated records, error records, total count and duplicates.
   */
  public validateRecordService(excelData: any[], msid: string, currentUser: string) {
    try {
      excelData = excelData.map(el => {
        return {
          ...el,
          Status: 'success',
          Message: ''
        }
      });

      const captureAllError = [];
      const TotalRecordsCount = excelData.length;
      const clonedExcelData = excelData.map(el => ({ ...el }));
      let duplicateRecords: any;

      let validaterecords = [];
      const finalSuccessRecords = [];
      const finalErrorRecords = [];
      const errorRecords = [];
      const successRecords = [];

      if (clonedExcelData && clonedExcelData.length > 0) {
        //Validate for Duplicate Records
        const uniqueKeys = this.commonService.appdemoAllFields();
        const { uniques, duplicates } = this.commonService.findDuplicatesAndUniqueRecords(clonedExcelData, uniqueKeys); // { uniqueRecords, duplicates }

        duplicateRecords = duplicates;
        uniques.forEach((ele, index) => {
          // Validate all column values U type
          if (!ele['Update_Type(U)']) {
            ele['Update_Type(U)'] = '';
          }
          ele = this.modifyUpdateType(ele);
          ele = this.modifyApplicationType(ele);
          this.commonService.trimObjValues(ele);
          ele = this.transformIds(ele);

          const validatedRecord = this.ValidateLobSublobRecord(ele, index);
          if (!validatedRecord.status) {
            ele['Status'] = 'Fail';
            ele['Message'] = validatedRecord.message;
          } else {
            ele['Status'] = 'Success';
            ele['Message'] = '';
          }
          if (ele['Status'] === 'Fail') {
            ele['UIValidationFailed'] = true;
            finalErrorRecords.push(ele);

            // finalErrorRecords.push(this.prepareRowCommonModel(validatedRecord, msid, currentUser));
          } else {
            finalSuccessRecords.push(ele);
            const record = this.prepareRowCommonModel(ele, msid, currentUser);
            successRecords.push(record);
          }

        });
      }

      validaterecords = successRecords;
      // captureAllError.push(validaterecords.error);
      return { validaterecords, finalErrorRecords, finalSuccessRecords, TotalRecordsCount, duplicateRecords };
    } catch (error) {
      console.error('Error during validation:', error);
      return { validaterecords: [], successRecords: [], finalErrorRecords: [], finalSuccessRecords: [], TotalRecordsCount: 0, duplicateRecords: [] };
    }
  }

  /**
   * Runs field-level validations for a single demographic row, including
   * required headers and allowed Update_Type(U) values, and returns a
   * validation result object.
   *
   * @param excelRow A single row from the uploaded Excel data.
   * @param index Row index within the dataset, used for error context.
   * @returns Validation result with status flag, index and aggregated message.
   */
  public ValidateLobSublobRecord(excelRow: any, index: number) {
    const update_type = excelRow['Update_Type(U)'] ? excelRow['Update_Type(U)'].trim() : null;
    let requiredFields = [
      { key: 'Update_Type(U)', message: BULK_MAINTENANCE_ERROR_MESSAGES.APP_DEMOGRAPHIC.UPDATE_TYPE_EMPTY, nullCheck: true },
      { key: 'Update_Type(U)', message: BULK_MAINTENANCE_ERROR_MESSAGES.APP_DEMOGRAPHIC.UPDATE_TYPE_INVALID, validCheck: true },
      { key: 'GLOBAL APP ID', message: BULK_MAINTENANCE_ERROR_MESSAGES.APP_DEMOGRAPHIC.GLOBAL_APP_ID_MISSING, nullCheck: true },
      { key: 'LOB ID', message: BULK_MAINTENANCE_ERROR_MESSAGES.APP_DEMOGRAPHIC.LOB_ID_MISSING, nullCheck: true },
      { key: 'Sub LOB ID', message: BULK_MAINTENANCE_ERROR_MESSAGES.APP_DEMOGRAPHIC.SUB_LOB_ID_MISSING, nullCheck: true },
    ];
    let isAttributeUpdate = (update_type && update_type == this.BULK_APP_DEMOGRAPHICS_UPDATE_TYPE[1]) ? true : false;
    if (isAttributeUpdate) {
      requiredFields.push(
        { key: 'Category_ID', message: BULK_MAINTENANCE_ERROR_MESSAGES.APP_DEMOGRAPHIC.CATEGORY_ID_MISSING, nullCheck: true },
        { key: 'Application_Type_Id', message: BULK_MAINTENANCE_ERROR_MESSAGES.APP_DEMOGRAPHIC.APPLICATION_TYPE_ID_MISSING, nullCheck: true },
        { key: 'Application_Hosting_ID', message: BULK_MAINTENANCE_ERROR_MESSAGES.APP_DEMOGRAPHIC.APPLICATION_HOSTING_ID_MISSING, nullCheck: true }
      )
    }
    let obj = this.commonService.initializeValidationObject();

    // Check if row already has a fail status
    if (excelRow['Status'] && excelRow['Status'].toLowerCase() == 'fail') {
      return this.commonService.createErrorObject(index, excelRow['Message']);
    }

    // Perform all validations
    obj = this.commonService.validateFieldsAndUpdateType(requiredFields, excelRow, index, obj, this.BULK_APP_DEMOGRAPHICS_UPDATE_TYPE);
    return obj.status === false ? obj : { status: true, index, message: "" };

  }


  /**
   * Builds the common payload model for a validated row, mapping Excel
   * columns to the expected database field names and adding audit fields.
   *
   * @param excelData Validated Excel row.
   * @param msid Current user MSID.
   * @param currentUser Current user display name ("SN,GN").
   * @returns Object wrapping the original row and the transformed DB record.
   */
  public prepareRowCommonModel(excelData: any, msid: string, currentUser: string) {
    const name = currentUser.split(',');
    const givenName = name[1];
    const sn = name[0];
    const record = {
      'ASK GLOBAL ID': null,
      Lob_ID: null,
      SUB_LOB_ID: null,
      createdById: msid,
      createdByName: `${givenName},${sn}`,
      createdAt: new Date().toISOString(),
      update_Type: '',
      category_id: null,
      application_type_id: null,
      app_hosting_id: null,
      Message: null,
      Status: null,
      wqRequestBy: JSON.parse(sessionStorage.getItem('currentUser')),
    };

    Object.keys(excelData).map((col) => {
      if (excelData && excelData[col] && (record[this.TableColumnLOBMapping[col]] !== undefined)) {
        record[this.TableColumnLOBMapping[col]] = excelData[col];
      }
    });
    return { excelRecord: excelData, dbRecord: record };
  }

  /**
   * Checks backend access for the provided LOB/SubLOB combinations before
   * submitting LOB demographic updates.
   *
   * @param body Request payload for access validation.
   * @returns Observable of the access-check API response.
   */
  public checkAccessData(body: any): Observable<any> {
    body['userDetails'] = this.userDtls;
    const url = this.largeApiUrl + this.config.routerpath.businessLobSublobCheckAccess;
    const headers = new HttpHeaders({
      'Content-Type': ''
    });
    const options = {
      headers: headers
    }
    return this.http.put<any>(url, body, options);
  }

  /**
   * Submits validated LOB demographic records to the bulk-import API.
   *
   * @param body Request payload containing validated records.
   * @returns Observable of the bulk-import API response.
   */
  public submitLOBData(body: any): Observable<any> {
    const url = this.largeApiUrl + this.config.routerpath.businessLobSublobBulkImport;
    const headers = new HttpHeaders({
      'Content-Type': ''
    });
    const options = {
      headers: headers
    }
    return this.http.put<any>(url, body, options);
  }

  /**
   * Normalizes application-type and hosting fields, replacing null/empty
   * values with the sentinel value 'No Mapping'.
   *
   * @param item Row object to normalize.
   * @returns The same object instance with normalized application fields.
   */
  public modifyApplicationType(item: any) {
    const applicationType = item['Application_Type_Id'];
    const applicationHosting = item['Application_Hosting_ID'];

    if (this.commonService.NullCheck(applicationType)) {
      item['Application_Type_Id'] = 'No Mapping';
    }

    if (this.commonService.NullCheck(applicationHosting)) {
      item['Application_Hosting_ID'] = 'No Mapping';
    }

    return item;
  }

}